import { v4 as uuidv4 } from "uuid";
import { ItemVariant, IngredientOption, ComboVariant, MenuItem, BaseItem, findBaseItemAndVariant, FullMenuPageData, BaseItemVariants, Bundle } from "./fullMenuPageData";

export interface SelectedIngredientOption {
  option: IngredientOption;
  quantity: number
}

export interface SelectedMenuItems {
  id: string;
  name: string;
  price: number; // display purposes only
  selectedVariants: ItemVariant[];
  selectedOptions: SelectedIngredientOption[];
}

// Represents a fully configured Menu item that has been added to the cart.
export interface MenuCartItem {
  id: string;
  baseItemId: string;
  price: number; // display purposes only
  items: SelectedMenuItems[];
  comboVariant?: ComboVariant; // So we can know if the item has already been processed with the top level price of the menu combo
  bundle?: Bundle;
  addedAt: Date;
  name: string;
}

// Represents the entire shopping cart.
export interface MenuCart {
  items: MenuCartItem[];
  // The grand total for all items in the cart.
  grandTotalBeforeFees: number;
  restaurantId: string;
}

export interface TemporaryMenuCartStoreType {
  cart: MenuCart,
  profileId: string,
  updatedAt: Date,
  createdAt: Date
}

// Helper function to calculate the price of a single CartItem.
export function calculateCartItemPrice(selectedMenuItems: SelectedMenuItems[], isCombo: boolean): number {
  let price = 0;
  selectedMenuItems.forEach(item => {
    price += item.price;
    item.selectedOptions.forEach(op => {
      price += op.option.price
    })
    item.selectedVariants.forEach(vr => {
      if (isCombo) {
        price += vr.comboPrice
      } else {
        price += vr.price
      }
    })
  })
  return price
}

/** Compute price variation for one base item selection (price for selected variants + ingredients). */
export function computeBaseItemSelectionPriceVariation(base: BaseItem, variantsChosen: (ItemVariant | undefined)[], optionsChosen: SelectedIngredientOption[], inCombo: boolean) {
  let sum = 0;

  for (const sel of variantsChosen) {
    if (sel) sum += inCombo ? (sel.comboPrice || 0) : (sel.price || 0);
  }
  for (const ing of optionsChosen) {
    sum += (ing.quantity || 0) * (ing.option.price || 0);
  }
  return sum;
}


function getLowestPriceOfVariantsAndOptionsOfItem(item: BaseItem, isCombo: boolean): number {
  let price = 0;
  item.variants.forEach(variant => {
    if (variant.variants.length > 0) {
      if (isCombo)
        price += variant.variants.reduce((min, v) => v.comboPrice < min.comboPrice ? v : min).comboPrice
    } else {
      price += variant.variants.reduce((min, v) => v.price < min.price ? v : min).price
    }
  })
  // options are ignored because you dont need to choose them
  return price
}


export function cheapestPriceOfComboVariant(menuData: FullMenuPageData, comboVariant: ComboVariant) {
  let price = comboVariant.price
  comboVariant.itemVariantIds.forEach(cvi => {
    const item = menuData.baseItems.find(bi => bi.id == cvi)
    if (item) {
      price += getLowestPriceOfVariantsAndOptionsOfItem(item, true)
    }
  })
  return price
}

export function getLowestPriceOfMenuItem(menuItem: MenuItem, menuData: FullMenuPageData) {
  if (menuItem.isCombo && menuItem.comboVariants) {
    let price: number | null = null
    menuItem.comboVariants.forEach(cv => {
      const priceOfCV = cheapestPriceOfComboVariant(menuData, cv)
      if (price == null || price > priceOfCV) {
        price = priceOfCV
      }
    })
    return price ? price : 0
  } else {

    const baseItem = menuData.baseItems.find(bi => bi.id === menuItem.baseItemId);
    if (!baseItem) return 0
    return getLowestPriceOfVariantsAndOptionsOfItem(baseItem, false) + baseItem.price
  }
}

export function getPriceOfMenuCart(items: MenuCartItem[], menuData: FullMenuPageData) {
  let price = 0
  let topLevelPriceAddedForCombos: Map<string, boolean> = new Map();
  let topLevelPriceAddedForBundles: Map<string, boolean> = new Map();
  console.log("items", items)
  items.forEach((menuCartItem: MenuCartItem) => {
    const menuCartItemBaseItemId = menuCartItem.id.split(".")[0]
    menuCartItem.items.forEach(item => {
      const baseItem = menuData.baseItems.find(mbi => mbi.id == item.id)
      if (baseItem) {
        price += computeBaseItemSelectionPriceVariation(baseItem, item.selectedVariants, item.selectedOptions, menuCartItem.comboVariant != undefined);
        if (menuCartItem.bundle == undefined && menuCartItem.comboVariant == undefined) {
          price += baseItem.price
        }
      }
    })

    if (menuCartItem.bundle && !topLevelPriceAddedForBundles.has(menuCartItem.bundle.id)) {
      menuCartItem.bundle.items.forEach(bundleItem => {
        if (bundleItem.menuItemId == menuCartItemBaseItemId) {
          if (!topLevelPriceAddedForBundles.has(menuCartItem.bundle!.id)) {
            if (bundleItem.noVariantPriceOverride) {
              price += bundleItem.noVariantPriceOverride
              topLevelPriceAddedForBundles.set(menuCartItem.bundle!.id, true)
            } else {
              if (menuCartItem.comboVariant) {
                bundleItem.variantPriceOverrides.forEach(vpo => {
                  if (!topLevelPriceAddedForBundles.has(vpo.variantId)) {
                    if (vpo.variantId == menuCartItem.comboVariant!.id) {
                      price += vpo.price
                      topLevelPriceAddedForBundles.set(vpo.variantId, true)
                    }
                  }
                })
              }
            }
          }
        }
      });
    }

    if (!menuCartItem.bundle && menuCartItem.comboVariant && !topLevelPriceAddedForCombos.has(menuCartItem.comboVariant.id)) {
      price += menuCartItem.comboVariant.price
    }
    console.log("price", price)
  })
  return price
}

export interface IToCart {
  menuItem: MenuItem;
  comboVariant: ComboVariant | null
  menuData: FullMenuPageData;
  comboSelections: Record<string, {
    variantsByGroupId: Record<string, ItemVariant | undefined>;
    ingredients: SelectedIngredientOption[];
  }>
  baseItem: BaseItem;
  bundle?: Bundle,
  selectedVariantsByGroup: Record<string, ItemVariant | undefined>;
  selectedIngredientOptions: SelectedIngredientOption[];
}

// Build cart payload
export const toMenuCart = (
  { menuItem, comboVariant, menuData, comboSelections, baseItem, selectedVariantsByGroup, selectedIngredientOptions, bundle }: IToCart
): MenuCartItem => {
  if (menuItem?.isCombo && comboVariant) {
    const subSelections: SelectedMenuItems[] = []
    comboVariant.itemVariantIds.forEach(ivID => {
      const resolved = findBaseItemAndVariant(menuData, ivID);
      const item = resolved.baseItem;
      const sel = item ? comboSelections[item.id] : undefined;
      if (item && sel) {
        let selectedVariants: ItemVariant[] = [];
        item.variants.forEach((vr: BaseItemVariants) => {
          const variant = sel.variantsByGroupId[vr.id]
          if (variant) {
            selectedVariants.push(variant)
          }
        })

        subSelections.push({
          id: item.id,
          name: item.name,
          price: item.price,
          selectedOptions: sel.ingredients.map(ing => { return { ...ing } }),
          selectedVariants: selectedVariants
        })
      }
    });
    let cartItem: MenuCartItem = {
      baseItemId: comboVariant.id,
      id: `${menuItem.id}.${uuidv4()}`,
      comboVariant: comboVariant,
      items: subSelections,
      price: calculateCartItemPrice(subSelections, menuItem.isCombo),
      addedAt: new Date(),
      name: baseItem.name + " - " + comboVariant.name
    }
    if (bundle) {
      cartItem = { ...cartItem, bundle: bundle }
    }

    return cartItem;
  }

  let selectedVariants: ItemVariant[] = [];
  baseItem.variants.forEach((vr: BaseItemVariants) => {
    const variant = selectedVariantsByGroup[vr.id]
    if (variant) {
      selectedVariants.push(variant)
    }
  })

  const selectedOptions: SelectedIngredientOption[] = []
  selectedIngredientOptions.forEach(svbg => {
    if (svbg.quantity > 0) {
      selectedOptions.push({ ...svbg })
    }
  })

  const selectedItems: SelectedMenuItems = {
    id: baseItem.id,
    name: baseItem.name,
    price: baseItem.price,
    selectedOptions: selectedOptions,
    selectedVariants: selectedVariants
  }

  // single item cart payload
  let cartItem: MenuCartItem = {
    baseItemId: baseItem.id,
    id: `${menuItem.id}.${uuidv4()}`,
    items: [selectedItems],
    price: calculateCartItemPrice([selectedItems], false),
    addedAt: new Date(),
    name: baseItem.name
  }
  if (bundle) {
    cartItem = { ...cartItem, bundle: bundle }
  }

  return cartItem;
};